import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { verifyMemberTrackerPasswordApi } from "src/DAL/tracker/tracker";
import { PRIMARY } from "./memberTrackersStorage";

const MUTED = "#64748b";

/** True when a member journey API response means "ask for the password". */
export const isJourneyPasswordRequired = (res) =>
  res?.code === 403 && res?.requirePassword === true;

/** Small "Password protected" chip for journey cards. */
export const JourneyLockChip = () => (
  <Chip
    size="small"
    icon={<LockOutlinedIcon sx={{ fontSize: "14px !important" }} />}
    label="Password protected"
    sx={{
      height: 24,
      fontWeight: 700,
      fontSize: 11.5,
      bgcolor: "#f1f5f9",
      color: MUTED,
      "& .MuiChip-icon": { color: MUTED },
    }}
  />
);

/**
 * Password popup for a protected journey (detail + check-in pages).
 * Calls onUnlocked() after the backend accepts the password.
 * Closing / Cancel returns to the journeys list.
 */
export default function JourneyPasswordGate({ idOrSlug, tracker, onUnlocked }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const goBack = () => navigate("/trackers");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await verifyMemberTrackerPasswordApi(idOrSlug, { password });
      if (res?.code === 200) {
        onUnlocked?.();
        return;
      }
      setError(res?.message || "Could not verify the password. Please try again.");
    } catch (_err) {
      setError("Could not verify the password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid">
      <Dialog
        open
        onClose={submitting ? undefined : goBack}
        disableEscapeKeyDown={submitting}
        maxWidth="xs"
        fullWidth
        aria-labelledby="journey-password-title"
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <DialogTitle
            id="journey-password-title"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              fontWeight: 800,
              color: "inherit",
              pb: 1,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: "11px",
                bgcolor:
                  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)",
                color: PRIMARY,
                display: "grid",
                placeItems: "center",
              }}
            >
              <LockOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.25 }}>
              {tracker?.name || "Password required"}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography
              sx={{ color: MUTED, fontSize: 14.5, lineHeight: 1.5, mb: 2 }}
            >
              This journey is password protected. Enter the password from your
              coach to continue.
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Password"
              autoFocus
              autoComplete="current-password"
              value={password}
              disabled={submitting}
              error={Boolean(error)}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError("");
              }}
            />
            {error ? (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {error}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <button
              type="button"
              className="small-contained-outlined-button"
              disabled={submitting}
              onClick={goBack}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="small-contained-button"
              disabled={!password || submitting}
              style={{
                opacity: !password || submitting ? 0.55 : 1,
                minWidth: 120,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              {submitting ? (
                <CircularProgress size={14} color="inherit" />
              ) : null}
              {submitting ? "Unlocking…" : "Unlock"}
            </button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
}
